import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { MappingDependency } from '@classes/mapping/mapping-dependency';
import { MappingTree } from '@classes/mapping/mapping-tree';
import { SubscriptionBaseComponent } from '@components/subscription-base/subscription-base.component';
import { TreeItemMode } from '@enums/tree-item-mode.enum';
import { DragDropItem } from '@interfaces/drag-drop-item';
import { MappingService } from '@services/mapping.service';
import { UiService } from '@services/ui.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-tree-items',
    templateUrl: './tree-items.component.html',
    styleUrls: ['./tree-items.component.scss']
})
export class TreeItemsComponent implements OnInit, OnDestroy {
    @Input() canEditMapping: boolean;
    @Input() treeItems: MappingTree[] = [];
    @Input() depsLabel: string;
    @Input() isRoot: boolean;
    @Input() displayValidity: boolean;
    @Output() listChanged = new EventEmitter<MappingTree[]>();

    ItemMode = TreeItemMode;
    isCreationMode: boolean;
    draggedOver: MappingTree;

    dateFormat = 'MM-yyyy';
    pipeCounter = 0;

    private dragged: MappingTree[];
    private dropTo: MappingTree;

    private unsubscribeAll = new Subject<void>();

    constructor(
        private uiSrv: UiService,
        private mappingSrv: MappingService
    ) { }

    ngOnInit(): void {
        this.orderTreeItems();
        this.initSubscriptions();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    onToggleItem(item: MappingTree): void {
        item.isOpen = !item.isOpen;
    }

    /**
     * Open add form to create new node
     */
    onAddStarted(): void {
        this.isCreationMode = true;
        this.cleanEditItems();
    }

    /**
     * Push new node in tree
     * @param item new node to push
     */
    onAddFinished(addedItem?: MappingTree): void {
        if (addedItem) {
            const newItem = new MappingTree(addedItem.label, addedItem.description, this.treeItems.length - 1);
            this.treeItems.push(newItem);

            this.listChanged.emit(this.treeItems);
        }

        this.isCreationMode = false;
    }

    /**
     * Open edit form to edit selected node
     * @param item selected node to edit
     */
    onEditStarted(item: MappingTree): void {
        this.cleanEditItems();
        this.isCreationMode = false;

        item.isEditMode = true;
        item.isOpen = false;
    }

    /**
     * Change edit mode of item
     * Emit updated list to parent
     * @param item item user just finished to edit
     */
    onEditFinished(item: MappingTree, editedItem?: MappingTree): void {
        if (editedItem) {
            if (editedItem.text) {
                item.text.fullLabel = editedItem.text.fullLabel;
                item.text.detail = editedItem.text.detail;
            } else {
                item.label = editedItem.label;
                item.description = editedItem.description;
            }

            if (editedItem.properties) {
                item.properties = [...editedItem.properties];
            }
        }

        item.isEditMode = false;
        this.listChanged.emit(this.treeItems);
    }

    onDelete(item: MappingTree): void {
        const message = 'mapping.base.tree.confirmDelete.summary';

        this.uiSrv.confirmDialog(message).afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    this.unmapDependencies(item);
                    this.treeItems = this.treeItems.filter(i => i !== item);
                    this.listChanged.emit(this.treeItems);
                }
            });
    }

    /**
     * Update children array from child component changes
     * @param items new array of children
     * @param changedItem item to update
     */
    onChildrenChanged(items: MappingTree[], changedItem: MappingTree): void {
        changedItem.children = [...items];
        this.listChanged.emit(this.treeItems);
    }

    /**
     * Update dependencies array from child component changes
     * @param deps new array of dependencies
     * @param changedItem item to update
     */
    onDepsChanged(deps: MappingDependency[], changedItem: MappingTree): void {
        changedItem.dependencies = [...deps];
        this.listChanged.emit(this.treeItems);
    }

    /**
     * By default, data/elements cannot be dropped in other elements.
     * Prevent the default handling of the element.
     * @param event DragEvent
     */
    allowDrop(event: DragEvent) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }

    /**
     * Specifies what data to be dragged.
     * Update draggedItemChanged subject
     * @param event DragEvent
     * @param selectedItem TreeItem
     */
    onDrag(event: DragEvent, selectedItem: MappingTree): void {
        // Fix for Firefox support
        event.dataTransfer.setData('data', selectedItem.id ? selectedItem.id.toString() : 'newItem');
        event.dataTransfer.dropEffect = "move";

        this.mappingSrv.dragItem({ items: [selectedItem], type: 'tree' });
        this.dragged = [selectedItem];
    }

    /**
     * Used to know over wich element we are dragging item to adapt it's css
     * @param event DragEvent
     * @param selectedItem If no item, we are leaving element
     */
    onDragOver(event: DragEvent, selectedItem?: MappingTree): void {
        const target = event.target as HTMLTextAreaElement;
        const relatedTarget = event.relatedTarget as HTMLTextAreaElement;

        if (relatedTarget && (relatedTarget.nodeType === 3 || target === relatedTarget)) {
            return;
        }

        this.draggedOver = selectedItem;
    }

    /**
     * When the dragged data is dropped.
     * Update tree or dependencies depending of draggedItem type
     * @param event DragEvent
     * @param toItem TreeItem
     */
    onDrop(event: DragEvent, dropTo: MappingTree): void {
        if (this.canEditMapping) {
            const draggedItem: DragDropItem = this.mappingSrv.dragged;

            switch (draggedItem.type) {
                case 'tree':
                    this.dropTreeNode(dropTo, draggedItem.items[0] as MappingTree);
                    break;
                case 'dep':
                default:
                    this.dropDep(dropTo, draggedItem.items as MappingDependency[]);
            }

            this.draggedOver = null;
            event.preventDefault();
        }
    }

    moveUp(index: number): void {
        this.treeItems.splice(index - 1, 0, this.treeItems.splice(index, 1)[0]);
        this.treeItems = this.treeItems.map((item, index) => {
            item.displayOrder = index;
            return item;
        });
        this.pipeCounter--;
        this.listChanged.emit(this.treeItems);
    }

    moveDown(index: number): void {
        this.treeItems.splice(index + 1, 0, this.treeItems.splice(index, 1)[0]);
        this.treeItems = this.treeItems.map((item, index) => {
            item.displayOrder = index;
            return item;
        });
        this.pipeCounter++;
        this.listChanged.emit(this.treeItems);
    }

    private orderTreeItems(): void {
        this.treeItems = this.treeItems.map((item, index) => {
            item.displayOrder = index;
            return item;
        });
    }

    private orderDependencies(treeItem: MappingTree): void {
        treeItem.dependencies.map((dep: MappingDependency, index: number) => {
            dep.displayOrder = index;
        })
    }

    private unmapDependencies(item: MappingTree): void {
        this.mappingSrv.unmapDependencies.next(item.dependencies);
        if (item.children) {
            item.children.map(c => this.unmapDependencies(c as MappingTree));
        }
    }

    // First, test if item can be dropped, dropping is done in subscription after item was removed form previous list
    // To avoid mess in update events
    private dropTreeNode(dropTo: MappingTree, draggedItem: MappingTree): void {
        draggedItem.displayOrder = dropTo.children.length;
        // avoid dropping in direct parent
        if (draggedItem !== dropTo && !dropTo.children.find(i => i === draggedItem)) {
            this.dropTo = dropTo;
            this.mappingSrv.droppedItemChanged.next(true);
        } else {
            this.dropTo = null;
            this.mappingSrv.droppedItemChanged.next(false);
        }
        this.orderTreeItems();
    }

    // Juste update dependecies array and emit array to parent
    private dropDep(dropTo: MappingTree, draggedItems: MappingDependency[]): void {
        draggedItems.forEach((item, index) => {
            item.displayOrder = dropTo.dependencies.length + index;
        });
        // Avoid dropping in same parent
        if (!this.isRoot && !dropTo.dependencies.find(d => draggedItems.find(i => i === d))) {
            dropTo.dependencies = [...dropTo.dependencies, ...draggedItems];
            this.mappingSrv.dragItem(null);
            this.listChanged.emit(this.treeItems);
            this.mappingSrv.droppedItemChanged.next(true);
        } else {
            this.mappingSrv.droppedItemChanged.next(false);
        }
        this.orderDependencies(dropTo);
    }

    private cleanEditItems(): void {
        this.treeItems.map(i => i.isEditMode = false);
    }

    private initSubscriptions(): void {
        // clean up source list when drop is completed
        this.mappingSrv.droppedItemChanged
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(hasDrop => {
                if (this.dragged && hasDrop) {
                    this.treeItems = this.treeItems.filter(i => !this.dragged.find(d => d === i));

                    this.dragged = null;
                    this.listChanged.emit(this.treeItems);
                }
            });

        // Drop stored item in array after it was removed from previous list
        this.mappingSrv.treeChanged
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(() => {
                if (this.dropTo) {
                    const draggedItem: DragDropItem = this.mappingSrv.dragged;
                    if (draggedItem && draggedItem.type === 'tree') {
                        // Update by reference then clean up property and emit array to parent
                        this.dropTo.children = [...this.dropTo.children, draggedItem.items[0] as MappingTree];
                        this.dropTo = null;
                        this.mappingSrv.dragItem(null);
                        this.listChanged.emit(this.treeItems);
                    }
                }
            })
    }
}
